/**
 * RBAC-4.2 · FR-31. "A new hire needs what Sarah has." Pick a colleague, see the difference between
 * their access and this person's (AC 1), apply it. A reserved permission is never copied and the
 * dialog says so (AC 3); every other refusal is named before confirming, with the confirm disabled.
 * The refusals themselves are the database's (AC 2).
 *
 * Copy lives in `lib/personAccessCopy.ts` (tested); this file only lays it out.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, Loader2, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  copyAccessView,
  filterCopySources,
  isStaleCopy,
  sourceOptionLabel,
  type CopyPreview,
  type CopySource,
} from '@/lib/personAccessCopy';
import { assignPersonRoleErrorMessage, type PersonRoleAssignment } from '@/lib/personRole';
import { copyPersonAccess, fetchCopyPreview, fetchCopySources } from '@/services/personAccessCopyService';
import { PersonRoleError } from '@/services/personRoleService';

export interface CopyAccessDialogProps {
  open: boolean;
  assignment: PersonRoleAssignment;
  onOpenChange: (open: boolean) => void;
  /** After a copy, or after a 22023 saying someone's role moved underneath: re-read the record. */
  onChanged: (roleId: number | null) => void;
}

const rpcOf = (e: unknown) => (e instanceof PersonRoleError ? e.rpc : { message: e instanceof Error ? e.message : undefined });

export function CopyAccessDialog({ open, assignment, onOpenChange, onChanged }: CopyAccessDialogProps) {
  const [sources, setSources] = useState<CopySource[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [preview, setPreview] = useState<CopyPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSources(null);
    setLoadError(null);
    setQuery('');
    setSourceId('');
    setPreview(null);
    setError(null);
    setStale(false);
    let cancelled = false;
    fetchCopySources(assignment.userId)
      .then((s) => !cancelled && setSources(s))
      .catch((e) => !cancelled && setLoadError(assignPersonRoleErrorMessage(rpcOf(e))));
    return () => {
      cancelled = true;
    };
  }, [open, assignment.userId]);

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!open || !sourceId) return;
    let cancelled = false;
    fetchCopyPreview(assignment.userId, sourceId)
      .then((p) => !cancelled && setPreview(p))
      .catch((e) => !cancelled && setPreviewError(assignPersonRoleErrorMessage(rpcOf(e))));
    return () => {
      cancelled = true;
    };
  }, [open, sourceId, assignment.userId]);

  const shown = useMemo(() => {
    const filtered = sources ? filterCopySources(sources, query) : [];
    // Keep the chosen person in the list while the query narrows past them, so the select never
    // silently shows a different name from the one previewed.
    const chosen = sources?.find((s) => s.userId === sourceId);
    return chosen && !filtered.includes(chosen) ? [chosen, ...filtered] : filtered;
  }, [sources, query, sourceId]);

  const view = preview ? copyAccessView({ preview, target: assignment }) : null;
  const name = assignment.firstName.trim() || 'this person';

  const confirm = async () => {
    if (!preview || !view?.canApply) return;
    setSaving(true);
    setError(null);
    try {
      const roleId = await copyPersonAccess(assignment, preview);
      onChanged(roleId);
      onOpenChange(false);
    } catch (e) {
      const rpc = rpcOf(e);
      setStale(isStaleCopy(rpc));
      setError(assignPersonRoleErrorMessage(rpc));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]" data-testid="copy-access-dialog">
        <DialogHeader>
          <DialogTitle>{view ? view.title : `Copy access to ${name}`}</DialogTitle>
          <DialogDescription>
            Pick someone who already does the job. You will see what changes for {view?.targetName ?? name} before anything is applied.
          </DialogDescription>
        </DialogHeader>

        {loadError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {loadError}
          </p>
        )}

        {!sources && !loadError && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {sources && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="copy-access-source" className="text-sm font-medium">
                Copy from
              </label>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or role"
                aria-label="Search people"
                disabled={saving}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {/* Native, not SearchableSelect: that one portals its list at z-300, under this
                  dialog's z-350, so it opens invisibly (RBAC-4.1). */}
              <select
                id="copy-access-source"
                value={sourceId}
                onChange={(e) => {
                  setSourceId(e.target.value);
                  setError(null);
                  setStale(false);
                }}
                disabled={saving}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">
                  {shown.length === 0 ? (query ? `Nobody matches "${query}"` : 'Nobody else in this account') : 'Choose a person'}
                </option>
                {shown.map((s) => (
                  <option key={s.userId} value={s.userId}>
                    {sourceOptionLabel(s)}
                  </option>
                ))}
              </select>
              {query && (
                <p className="text-xs text-muted-foreground" data-testid="copy-access-count">
                  {shown.length} of {sources.length}
                </p>
              )}
            </div>

            {sourceId && !preview && !previewError && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}

            {previewError && (
              <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {previewError}
              </p>
            )}

            {view?.canApply && (
              <div className="space-y-3 rounded-md border border-border p-3" data-testid="copy-access-diff">
                <p className="text-sm font-medium" data-testid="copy-access-summary">
                  {view.summary}
                </p>
                {view.noDifference && <p className="text-sm text-muted-foreground">{view.noDifference}</p>}
                {view.gaining.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {view.targetName} gains {view.gaining.length}
                    </p>
                    <ul className="space-y-1.5" data-testid="copy-access-gaining">
                      {view.gaining.map((l) => (
                        <li key={l.key} className="flex gap-2 text-sm">
                          <Plus className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                          <span>
                            <span className="font-medium">{l.title}</span>
                            <span className="text-muted-foreground"> · {l.sentence}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {view.losing.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {view.targetName} loses {view.losing.length}
                    </p>
                    <ul className="space-y-1.5" data-testid="copy-access-losing">
                      {view.losing.map((l) => (
                        <li key={l.key} className="flex gap-2 text-sm">
                          <Minus className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                          <span>
                            <span className="font-medium">{l.title}</span>
                            <span className="text-muted-foreground"> · {l.sentence}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {view.unlisted && (
                  <p className="text-xs text-muted-foreground" data-testid="copy-access-unlisted">
                    {view.unlisted}
                  </p>
                )}
              </div>
            )}

            {view?.refusal && (
              <div
                role="alert"
                data-testid="copy-access-refusal"
                className="flex gap-2.5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>{view.refusal}</p>
              </div>
            )}

            {view && view.notes.length > 0 && (
              <div
                className="flex gap-2 rounded-md border border-sky-200 bg-sky-50 p-2.5 text-sm text-sky-900 dark:border-sky-500/40 dark:bg-sky-950/40 dark:text-sky-100"
                data-testid="copy-access-notes"
              >
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <div className="space-y-1">
                  {view.notes.map((n) => (
                    <p key={n}>{n}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {stale ? (
            <Button
              onClick={() => {
                onChanged(null);
                onOpenChange(false);
              }}
            >
              Reload their record
            </Button>
          ) : (
            <Button onClick={() => void confirm()} disabled={!view?.canApply || saving} data-testid="copy-access-confirm">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {view ? view.confirmLabel : 'Copy access'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
